import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { catchError, lastValueFrom, of } from 'rxjs';
import {
  GoogleLoginProvider,
  SOCIAL_AUTH_CONFIG,
  SocialAuthServiceConfig,
} from '@abacritt/angularx-social-login';
import { routes } from '../../shared/routes/app.routes';
import { provideToastr } from 'ngx-toastr';
import { credentialsInterceptor } from '../interceptors/credentials.interceptor';
import { csrfInterceptor } from '../interceptors/csrf.interceptor';
import { unauthorizedInterceptor } from '../interceptors/unauthorized.interceptor';
import { AuthService } from '../../shared/services/auth.service';
import { environment } from '../../../environments/environment';

// Fetch a CSRF token on application startup (runs in injection context)
async function initCsrf(): Promise<void> {
  const auth = inject(AuthService);
  await lastValueFrom(auth.getCsrf().pipe(catchError(() => of(null))));
  return undefined;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([csrfInterceptor, credentialsInterceptor, unauthorizedInterceptor]),
    ),
    provideToastr({
      timeOut: 10000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
    provideAppInitializer(initCsrf),
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.googleClientID, {
              oneTapEnabled: false,
            }),
          },
        ],
        onError: (error: Error) => {
          console.error('[ERR]: error with google login provider', error);
        },
      } as SocialAuthServiceConfig,
    },
  ],
};
